import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, TextField, Button, Checkbox, FormControlLabel,
  Stack, Alert, InputAdornment, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';

import {
  createClient,
  updateClient,
  getClientById,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { PAIS_LIST } from '../utils/fiscalLabels';

// === estilos/padrão visual idêntico ao FormProduto ===
const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper'
  },
};
function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...zen.sectionCardSx, ...sx }}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

export default function FormCliente({
  isEditMode = false,
  clientId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const navigate = useNavigate();

  const id = clientId ?? params.id;
  const editing = isEditMode || !!clientId;

  const [form, setForm] = useState({
    cnpj: '',
    inscricaoEstadual: '',
    razaoSocial: '',
    nomeFantasia: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
    email: '',
    telefone: '',
    simplesNacional: true,
    ativo: true,
    indIEDest: 9,
    convenioBloqueado: false,
    convenioLimite: '',
    msgBloqueio: '',
    cadastroResumido: false,
    aceitaNovidades: false,
    dataNascimento: '',
    sexo: 'N',
    estrangeiro: false,
    idEstrangeiro: '',
    cPais: 1058,
    xPais: 'Brasil',
  });

  const [saving, setSaving] = useState(false);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const titulo = useMemo(
    () => (editing ? `Editar Cliente #${id}` : 'Cadastro de Cliente'),
    [editing, id]
  );

  useEffect(() => {
    (async () => {
      if (!editing) return;
      try {
        const { data } = await getClientById(id);
        setForm({
          cnpj: data.cnpj || '',
          inscricaoEstadual: data.inscricaoEstadual || '',
          razaoSocial: data.razaoSocial || '',
          nomeFantasia: data.nomeFantasia || '',
          logradouro: data.logradouro || '',
          numero: data.numero || '',
          complemento: data.complemento || '',
          bairro: data.bairro || '',
          cep: data.cep || '',
          cidade: data.cidade || '',
          estado: data.estado || '',
          email: data.email || '',
          telefone: data.telefone || '',
          simplesNacional: !!data.simplesNacional,
          ativo: data.ativo !== false,
          indIEDest: Number(data.indIEDest ?? 9),
          convenioBloqueado: !!data.convenioBloqueado,
          convenioLimite: data.convenioLimite != null ? String(data.convenioLimite) : '',
          msgBloqueio: data.msgBloqueio || '',
          cadastroResumido: !!data.cadastroResumido,
          aceitaNovidades: !!data.aceitaNovidades,
          dataNascimento: data.dataNascimento
            ? String(data.dataNascimento).slice(0, 10)
            : '',
          sexo: data.sexo || 'N',
          estrangeiro: !!data.estrangeiro,
          idEstrangeiro: data.idEstrangeiro || '',
          cPais: Number(data.cPais ?? 1058),
          xPais: data.xPais || 'Brasil',
        });
      } catch (e) {
        setErrorMessage(getApiErrorMessage(e, 'Não foi possível carregar o cliente.'));
      }
    })();
  }, [editing, id]);

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/clients');
  };

  const onChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const consultarCnpj = async () => {
    const cnpj = onlyDigits(form.cnpj);
    if (cnpj.length !== 14) {
      setErrorMessage('CNPJ inválido.');
      return;
    }
    setErrorMessage('');
    setLoadingCnpj(true);
    try {
      const resp = await fetch(`https://publica.cnpj.ws/cnpj/${cnpj}`);
      if (!resp.ok) throw new Error('CNPJ não encontrado');
      const data = await resp.json();

      const est = data?.estabelecimento || {};
      const primeiraIE = est?.inscricoes_estaduais?.[0]?.inscricao_estadual || '';

      setForm((prev) => ({
        ...prev,
        razaoSocial: data?.razao_social || prev.razaoSocial,
        nomeFantasia: est?.nome_fantasia || prev.nomeFantasia,
        logradouro: est?.logradouro || prev.logradouro,
        numero: est?.numero || prev.numero,
        complemento: est?.complemento || prev.complemento,
        bairro: est?.bairro || prev.bairro,
        cep: est?.cep || prev.cep,
        cidade: est?.cidade?.nome || prev.cidade,
        estado: est?.estado?.sigla || prev.estado,
        email: est?.email || prev.email,
        telefone: est?.telefone1 || prev.telefone,
        inscricaoEstadual: primeiraIE || prev.inscricaoEstadual,
        simplesNacional: (data?.simples?.simples || '').toLowerCase() === 'sim',
      }));
    } catch (e) {
      setErrorMessage(e.message || 'Erro ao consultar CNPJ.');
    } finally {
      setLoadingCnpj(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSaving(true);
    try {
      const doc = onlyDigits(form.cnpj);
      const tel = onlyDigits(form.telefone);
      const resumido = !!form.cadastroResumido || (!form.logradouro?.trim() && !form.cidade?.trim());
      if (!form.estrangeiro) {
        if (doc && doc.length !== 11 && doc.length !== 14) {
          throw new Error('CPF/CNPJ inválido.');
        }
        if (!resumido) {
          if (!doc) throw new Error('CPF/CNPJ é obrigatório.');
          if (!form.razaoSocial?.trim()) throw new Error('Razão Social é obrigatória.');
          if (!form.nomeFantasia?.trim()) throw new Error('Nome Fantasia é obrigatório.');
          if (!form.logradouro?.trim() || !form.numero?.trim() || !form.bairro?.trim()) {
            throw new Error('Endereço incompleto (logradouro, número, bairro).');
          }
          if (!form.cidade?.trim() || !form.estado?.trim() || !form.cep?.trim()) {
            throw new Error('Cidade/Estado/CEP são obrigatórios.');
          }
        } else if (!form.razaoSocial?.trim() && !form.nomeFantasia?.trim() && tel.length < 10) {
          throw new Error('Informe nome ou um telefone com DDD.');
        }
      } else {
        if (!form.razaoSocial?.trim() && !form.nomeFantasia?.trim()) {
          throw new Error('Nome do cliente estrangeiro é obrigatório.');
        }
        if (!form.idEstrangeiro?.trim()) {
          throw new Error('Documento do estrangeiro é obrigatório (passaporte, ID fiscal, etc.).');
        }
        if (!form.cidade?.trim()) throw new Error('Cidade é obrigatória.');
        if (!form.xPais?.trim()) throw new Error('País é obrigatório.');
      }

      const payload = {
        cnpj: doc || null,
        inscricaoEstadual: form.inscricaoEstadual || null,
        razaoSocial: form.razaoSocial,
        nomeFantasia: form.nomeFantasia,
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento || null,
        bairro: form.bairro,
        cep: onlyDigits(form.cep) || null,
        cidade: form.cidade,
        estado: form.estado,
        email: form.email || null,
        telefone: onlyDigits(form.telefone) || null,
        simplesNacional: !!form.simplesNacional,
        ativo: !!form.ativo,
        indIEDest: Number(form.indIEDest),
        convenioBloqueado: !!form.convenioBloqueado,
        convenioLimite: form.convenioLimite === '' || form.convenioLimite == null
          ? null
          : Number(String(form.convenioLimite).replace(',', '.')),
        msgBloqueio: form.msgBloqueio || null,
        cadastroResumido: !!form.cadastroResumido,
        aceitaNovidades: !!form.aceitaNovidades,
        dataNascimento: form.dataNascimento || null,
        sexo: form.sexo || 'N',
        estrangeiro: !!form.estrangeiro,
        idEstrangeiro: form.estrangeiro ? form.idEstrangeiro.trim() : null,
        cPais: form.estrangeiro ? Number(form.cPais) : 1058,
        xPais: form.estrangeiro ? form.xPais.trim() : 'Brasil',
        estado: form.estrangeiro ? 'EX' : form.estado,
      };

      let savedId = editing ? Number(id) : null;
      if (editing) {
        await updateClient(id, payload);
      } else {
        const { data } = await createClient(payload);
        savedId = data?.id ?? null;
      }
      if (onSaved) onSaved(editing ? 'Cliente atualizado' : 'Cliente criado', savedId);
      else navigate('/clients');
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{ p: embedded ? 0 : 2, maxWidth: embedded ? 'none' : 1200, mx: 'auto' }}
      component="form"
      onSubmit={onSubmit}
    >
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          {titulo}
        </Typography>
      )}

      {form.cadastroResumido && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cadastro resumido (PDV/WhatsApp). Complete os dados quando possível.
        </Alert>
      )}
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      <Section title="Identificação" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.estrangeiro}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      estrangeiro: checked,
                      estado: checked ? 'EX' : prev.estado,
                      cPais: checked && prev.cPais === 1058 ? 2496 : (checked ? prev.cPais : 1058),
                      xPais: checked && prev.xPais === 'Brasil' ? 'Estados Unidos' : (checked ? prev.xPais : 'Brasil'),
                      indIEDest: checked ? 9 : prev.indIEDest,
                    }));
                  }}
                />
              }
              label="Cliente estrangeiro (exterior)"
            />
          </Grid>
          {!form.estrangeiro ? (
            <Grid item xs={12} md={4}>
              <TextField
                label="CPF/CNPJ"
                value={form.cnpj}
                onChange={onChange('cnpj')}
                fullWidth
                placeholder="CPF ou CNPJ"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={consultarCnpj}
                        variant="contained"
                        size="small"
                        disabled={loadingCnpj}
                        sx={{ textTransform: 'none' }}
                      >
                        {loadingCnpj ? 'Buscando...' : 'Buscar CNPJ'}
                      </Button>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={4}>
              <TextField
                label="Documento do estrangeiro"
                value={form.idEstrangeiro}
                onChange={onChange('idEstrangeiro')}
                fullWidth
                placeholder="Passaporte, tax ID, etc."
                helperText="Identificação fiscal no país de origem"
              />
            </Grid>
          )}
          {!form.estrangeiro && (
            <Grid item xs={12} md={4}>
              <TextField
                label="Inscrição Estadual"
                value={form.inscricaoEstadual}
                onChange={onChange('inscricaoEstadual')}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Indicador de IE do destinatário</InputLabel>
              <Select
                label="Indicador de IE do destinatário"
                value={form.indIEDest}
                onChange={(e) => setForm((prev) => ({ ...prev, indIEDest: Number(e.target.value) }))}
              >
                <MenuItem value={1}>Contribuinte ICMS</MenuItem>
                <MenuItem value={2}>Contribuinte isento</MenuItem>
                <MenuItem value={9}>Não contribuinte</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox checked={form.simplesNacional} onChange={onChange('simplesNacional')} />
              }
              label="Simples Nacional"
            />
            <FormControlLabel
              control={
                <Checkbox checked={form.ativo} onChange={onChange('ativo')} />
              }
              label="Ativo"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Razão Social / Nome"
              value={form.razaoSocial}
              onChange={onChange('razaoSocial')}
              fullWidth
              required={!form.cadastroResumido}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nome Fantasia"
              value={form.nomeFantasia}
              onChange={onChange('nomeFantasia')}
              fullWidth
              required={!form.cadastroResumido}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title="Endereço" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Logradouro" value={form.logradouro} onChange={onChange('logradouro')} fullWidth required={!form.cadastroResumido} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Número" value={form.numero} onChange={onChange('numero')} fullWidth required={!form.cadastroResumido} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Complemento" value={form.complemento} onChange={onChange('complemento')} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Bairro" value={form.bairro} onChange={onChange('bairro')} fullWidth required={!form.cadastroResumido} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="CEP" value={form.cep} onChange={onChange('cep')} fullWidth placeholder="00000-000" required={!form.cadastroResumido} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Cidade" value={form.cidade} onChange={onChange('cidade')} fullWidth required={!form.cadastroResumido} />
          </Grid>
          {form.estrangeiro ? (
            <>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>País</InputLabel>
                  <Select
                    label="País"
                    value={form.cPais}
                    onChange={(e) => {
                      const code = Number(e.target.value);
                      const pais = PAIS_LIST.find((p) => p.code === code);
                      setForm((prev) => ({
                        ...prev,
                        cPais: code,
                        xPais: pais?.name || prev.xPais,
                        estado: 'EX',
                      }));
                    }}
                  >
                    {PAIS_LIST.filter((p) => p.code !== 1058).map((p) => (
                      <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField label="UF" value="EX" fullWidth disabled helperText="Exterior" />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={1}>
                <TextField label="UF" value={form.estado} onChange={onChange('estado')} fullWidth inputProps={{ maxLength: 2 }} required={!form.cadastroResumido} />
              </Grid>
            </>
          )}
        </Grid>
      </Section>

      <Section title="Contato" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Email" type="email" value={form.email} onChange={onChange('email')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Telefone" value={form.telefone} onChange={onChange('telefone')} fullWidth placeholder="(00) 00000-0000" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Nascimento"
              type="date"
              value={form.dataNascimento}
              onChange={onChange('dataNascimento')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Sexo</InputLabel>
              <Select
                label="Sexo"
                value={form.sexo}
                onChange={(e) => setForm((prev) => ({ ...prev, sexo: e.target.value }))}
              >
                <MenuItem value="N">Não informado</MenuItem>
                <MenuItem value="M">Masculino</MenuItem>
                <MenuItem value="F">Feminino</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox checked={form.aceitaNovidades} onChange={onChange('aceitaNovidades')} />
              }
              label="Aceita receber novidades"
            />
          </Grid>
        </Grid>
      </Section>

      <Section title="Convênio">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Limite de crédito"
              value={form.convenioLimite}
              onChange={onChange('convenioLimite')}
              fullWidth
              placeholder="0.00"
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox checked={form.convenioBloqueado} onChange={onChange('convenioBloqueado')} />
              }
              label="Bloqueado para convênio"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="Mensagem de bloqueio"
              value={form.msgBloqueio}
              onChange={onChange('msgBloqueio')}
              fullWidth
              disabled={!form.convenioBloqueado}
              placeholder="Exibido no PDV quando o cliente estiver bloqueado"
            />
          </Grid>
          {editing && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (onCancel) onCancel();
                  navigate(`/convenios?clienteId=${id}`);
                }}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Abrir extrato de convênio
              </Button>
            </Grid>
          )}
        </Grid>
      </Section>

      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
        <Button
          id="btn-save"
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={<SaveIcon />}
          sx={{ minWidth: 160, background: zen.blue, '&:hover': { background: zen.blueDark }, textTransform: 'none', fontWeight: 700 }}
        >
          {editing ? 'Salvar Alterações' : 'Cadastrar Cliente'}
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={fechar}
          startIcon={<CancelIcon />}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Cancelar
        </Button>
      </Stack>
    </Box>
  );
}
